import type { SluggedId } from "Models";
import toast from "react-hot-toast";
import { cn } from "@/lib/utils";
import { styles } from "../styles";
import { createPortal } from "react-dom";
import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { ApiError, Field, FieldLabel } from "./fieldPrimitives";
import {
  XIcon,
  SearchIcon,
  Loader2,
  UserIcon,
  UsersIcon,
  CheckCircle2,
} from "lucide-react";
import { useSearchFacilityUsersQuery } from "../../api/endpoints/roster";
import { useAddCompleteLeagueTeamMutation } from "../../api/endpoints/teams";
import { RosterTeeType } from "../../enums";
import { useGetCurrentLocalFacilityId } from "@/modules/facility/facilityHooks";
import type { Clubhouse_LeagueV2PhoneSearchUserDto } from "../../types/leagueRoster";
import { extractServerErrorLines, teeSetIdOf } from "../helpers";
import { Gender } from "@/types/enums";
import { LeguesGender } from "../../enums";
import { OpenRoundCompletionNotice } from "../common/OpenRoundCompletionNotice";

interface AddTeamDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  leagueId: string;
  /** League's home facility; falls back to the current facility when omitted. */
  facilityId?: string;
  /** Roster user ids, slugged, as the search results are. */
  existingUserIds?: SluggedId[];
  /** Full-time players per team. Drives how many slots this form asks for. */
  teamSize?: number;
  /** Suggested name, so the operator usually just presses Add Team. */
  suggestedTeamName?: string;
  /** Men's league tee from Season Setup. Pre-fills men. */
  leagueMensTee?: RosterTeeType | null;
  /** Women's league tee from Season Setup. Pre-fills women. */
  leagueWomensTee?: RosterTeeType | null;
  /** Fired after the team lands, so the caller can clear its own UI state. */
  onAdded?: () => void;
  /**
   * Shown above Add Team while the current round is still open, so the
   * operator sees the completion consequence before the team is committed.
   */
  openRoundCompletionNotice?: string | null;
}

function isFemaleGender(gender?: string | number | null) {
  if (typeof gender === "number") return gender === Gender.FEMALE;
  if (gender == null) return false;
  const value = String(gender).trim().toLowerCase();
  return (
    value === String(LeguesGender.FEMALE).toLowerCase() ||
    value === "female" ||
    value === "woman" ||
    value === "women" ||
    value === "1"
  );
}

function teeFor(
  mens: RosterTeeType | null | undefined,
  womens: RosterTeeType | null | undefined,
  gender?: string | number | null,
): RosterTeeType {
  return isFemaleGender(gender)
    ? (womens ?? RosterTeeType.RED)
    : (mens ?? RosterTeeType.WHITE);
}

function displayNameOf(user: Clubhouse_LeagueV2PhoneSearchUserDto) {
  return (
    user.displayName ??
    `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() ??
    ""
  );
}

/**
 * Add a whole team to a finalized team league.
 *
 * A team league holds exactly `teamSize` full-time players per team, so after
 * finalization it cannot grow one player at a time — a single add would leave
 * an odd roster and a player on no team, which is a state the league is never
 * allowed to persist. This form therefore collects every player the team needs
 * before it will submit, and sends them as one request that the server commits
 * or rolls back whole.
 *
 * It only picks existing players. Creating a brand-new player is still the Add
 * Player dialog's job; that flow writes a roster row of its own, which is the
 * thing this operation exists to avoid doing by halves.
 */
export function AddTeamDialog({
  open,
  onOpenChange,
  leagueId,
  facilityId: facilityIdProp,
  existingUserIds,
  teamSize = 2,
  suggestedTeamName,
  leagueMensTee = RosterTeeType.WHITE,
  leagueWomensTee = RosterTeeType.RED,
  onAdded,
  openRoundCompletionNotice,
}: AddTeamDialogProps) {
  const currentFacilityId = useGetCurrentLocalFacilityId();
  const facilityId = facilityIdProp ?? currentFacilityId;

  const slotCount = Math.max(2, teamSize);

  const [teamName, setTeamName] = useState("");
  const [slots, setSlots] = useState<
    (Clubhouse_LeagueV2PhoneSearchUserDto | null)[]
  >(() => Array.from({ length: slotCount }, () => null));
  /** Which slot is currently being filled, or null while reviewing. */
  const [activeSlot, setActiveSlot] = useState<number | null>(0);
  const [searchInput, setSearchInput] = useState("");
  const [submittedQuery, setSubmittedQuery] = useState("");
  const [nameError, setNameError] = useState<string | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);

  const [addCompleteTeam, { isLoading: isSaving }] =
    useAddCompleteLeagueTeamMutation();

  useEffect(() => {
    if (!open) return;
    setTeamName(suggestedTeamName ?? "");
    setSlots(Array.from({ length: slotCount }, () => null));
    setActiveSlot(0);
    setSearchInput("");
    setSubmittedQuery("");
    setNameError(null);
    setApiError(null);
  }, [open, slotCount, suggestedTeamName]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") onOpenChange(false);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onOpenChange]);

  const { data: searchData, isFetching: isSearching } =
    useSearchFacilityUsersQuery(
      { facilityId: facilityId ?? "", query: submittedQuery },
      { skip: !facilityId || !submittedQuery || activeSlot === null },
    );

  // The API returns a single object or an array depending on result count.
  const searchResults: Clubhouse_LeagueV2PhoneSearchUserDto[] = useMemo(() => {
    const rawMatches = searchData?.matches;
    const rawMatch = searchData?.match;
    if (Array.isArray(rawMatches)) return rawMatches;
    if (Array.isArray(rawMatch)) return rawMatch;
    return rawMatch ? [rawMatch] : [];
  }, [searchData]);

  const filledSlots = slots.filter(
    (slot): slot is Clubhouse_LeagueV2PhoneSearchUserDto => slot !== null,
  );
  const isComplete = filledSlots.length === slotCount;

  /**
   * Already on the roster, or already standing in another slot of this form —
   * both would make the request fail, and the second is the easier mistake to
   * make because the operator has to search twice.
   */
  function unavailableReason(
    user: Clubhouse_LeagueV2PhoneSearchUserDto,
  ): string | null {
    if (user.userId == null) return null;
    if ((existingUserIds ?? []).includes(user.userId)) {
      return "Already in league";
    }
    const takenBy = slots.findIndex(
      (slot, index) => index !== activeSlot && slot?.userId === user.userId,
    );
    return takenBy === -1 ? null : `Already Player ${takenBy + 1}`;
  }

  function handleSearch() {
    const query = searchInput.trim();
    if (query.length < 2) return;
    setSubmittedQuery(query);
  }

  function handleSelectUser(user: Clubhouse_LeagueV2PhoneSearchUserDto) {
    if (activeSlot === null) return;
    const slot = activeSlot;
    setSlots((current) =>
      current.map((existing, index) => (index === slot ? user : existing)),
    );
    setSearchInput("");
    setSubmittedQuery("");
    setApiError(null);
    // Straight on to the next empty slot: an operator adding a team is going to
    // fill all of them, and stopping to press the next one is pure ceremony.
    const nextEmpty = slots.findIndex(
      (existing, index) => index !== slot && existing === null,
    );
    setActiveSlot(nextEmpty === -1 ? null : nextEmpty);
  }

  function handleClearSlot(index: number) {
    setSlots((current) =>
      current.map((existing, i) => (i === index ? null : existing)),
    );
    setSearchInput("");
    setSubmittedQuery("");
    setApiError(null);
    setActiveSlot(index);
  }

  async function handleSubmit() {
    if (!isComplete) return;
    const name = teamName.trim();
    if (!name) {
      setNameError("Team name is required");
      return;
    }
    setNameError(null);
    setApiError(null);

    try {
      await addCompleteTeam({
        facilityId: facilityId ?? "",
        leagueId,
        body: {
          teamName: name,
          players: filledSlots.map((user) => ({
            userId: user.userId!,
            playerName: displayNameOf(user) || undefined,
            // An established aG index is the player's current handicap; the
            // server seeds anyone who has none. Mirrors the single-player add.
            currentHandicap: user.agHandicap ?? undefined,
            teeSetId:
              teeSetIdOf(teeFor(leagueMensTee, leagueWomensTee, user.gender)) ??
              undefined,
          })),
        },
      }).unwrap();

      toast.success(`${name} added with ${slotCount} players.`);
      onAdded?.();
      onOpenChange(false);
    } catch (err: unknown) {
      // Kept in the dialog rather than thrown at a toast: nothing was saved, so
      // the operator can fix the problem the server named and press Add Team
      // again without rebuilding the team.
      const lines = extractServerErrorLines(err);
      setApiError(
        lines.join("\n") || "Couldn't add this team. Please try again.",
      );
    }
  }

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[200] grid place-items-center">
      <button
        type="button"
        aria-label="Dismiss dialog"
        className="absolute inset-0 bg-black/30 backdrop-blur-sm motion-safe:animate-in motion-safe:fade-in-0 motion-safe:duration-200"
        onClick={() => onOpenChange(false)}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Add Team"
        className="relative z-10 flex flex-col w-full max-w-lg max-h-[90vh] rounded-2xl bg-popover text-sm text-popover-foreground shadow-xl motion-safe:animate-in motion-safe:fade-in-0 motion-safe:zoom-in-95 motion-safe:slide-in-from-bottom-2 motion-safe:duration-200"
      >
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          className="absolute top-2 right-2 inline-flex items-center justify-center size-9 rounded-full bg-primary/6 text-primary hover:bg-primary/12 transition-colors"
        >
          <XIcon className="size-4" />
          <span className="sr-only">Close</span>
        </button>

        <div className="flex-1 overflow-y-auto px-6 pt-6 pb-6">
          <div className="flex items-center gap-2 mb-2">
            <h2 className="poppins text-xl font-bold leading-none tracking-tight">
              Add Team
            </h2>
          </div>
          <p className="mb-6 text-xs text-text-muted">
            A team joins with all {slotCount} of its players at once, so the
            league is never left holding a half-built team.
          </p>

          <div className="flex flex-col gap-4">
            {slots.map((slot, index) => {
              const isActive = activeSlot === index;
              return (
                <div key={index} className="flex flex-col gap-2">
                  <FieldLabel htmlFor={`team-player-${index}`}>
                    Player {index + 1}
                  </FieldLabel>

                  {slot ? (
                    <div className="flex items-center gap-3 rounded-md border border-border bg-surface/50 px-3 py-2">
                      <CheckCircle2 className="size-4 shrink-0 text-primary" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-text-primary poppins">
                          {displayNameOf(slot) || "Unknown"}
                        </p>
                        <p className="truncate text-xs text-text-muted">
                          {[slot.username, slot.email]
                            .filter(Boolean)
                            .join(" · ")}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleClearSlot(index)}
                        className="shrink-0 rounded-sm text-xs font-medium text-primary transition-colors hover:text-primary/80"
                      >
                        Change
                      </button>
                    </div>
                  ) : isActive ? (
                    <div className="flex flex-col gap-2">
                      <div className="flex gap-2">
                        <div className="relative flex flex-1 items-center">
                          <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-text-muted" />
                          <input
                            id={`team-player-${index}`}
                            type="text"
                            className={cn(styles.wizardInputCls, "pl-8")}
                            placeholder="Search by name, username, or phone"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleSearch();
                            }}
                          />
                        </div>
                        <Button
                          type="button"
                          onClick={handleSearch}
                          disabled={
                            isSearching || searchInput.trim().length < 2
                          }
                          className="shrink-0 rounded-md bg-primary font-bold text-white"
                        >
                          {isSearching ? (
                            <Loader2 className="size-4 animate-spin" />
                          ) : (
                            "Search"
                          )}
                        </Button>
                      </div>

                      {!!submittedQuery && !isSearching && (
                        <div className="overflow-hidden rounded-md border border-border">
                          {searchResults.length === 0 ? (
                            <p className="px-4 py-6 text-center text-sm text-text-muted">
                              No players found for &ldquo;{submittedQuery}
                              &rdquo;
                            </p>
                          ) : (
                            searchResults.map((user) => {
                              const blocked = unavailableReason(user);
                              return (
                                <button
                                  key={user.userId ?? 0}
                                  type="button"
                                  disabled={!!blocked}
                                  onClick={() => handleSelectUser(user)}
                                  className={cn(
                                    "flex w-full items-center gap-3 border-b border-border px-4 py-3 text-left transition-colors last:border-0",
                                    blocked
                                      ? "cursor-not-allowed opacity-60"
                                      : "hover:bg-surface/60",
                                  )}
                                >
                                  <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/10">
                                    <UserIcon className="size-4 text-primary" />
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <p className="truncate text-sm font-medium text-text-primary poppins">
                                      {displayNameOf(user) || "Unknown"}
                                    </p>
                                    <p className="truncate text-xs text-text-muted">
                                      {[
                                        user.username,
                                        user.email,
                                        user.phoneDigits,
                                      ]
                                        .filter(Boolean)
                                        .join(" · ")}
                                    </p>
                                  </div>
                                  {blocked && (
                                    <span className="poppins shrink-0 whitespace-nowrap rounded-full border border-border px-2 py-0.5 text-xs font-medium tracking-tight text-text-muted">
                                      {blocked}
                                    </span>
                                  )}
                                </button>
                              );
                            })
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setActiveSlot(index)}
                      className="flex items-center gap-2 rounded-md border border-dashed border-border px-3 py-2.5 text-left text-sm text-text-muted transition-colors hover:border-primary/40 hover:text-text-primary"
                    >
                      <UsersIcon className="size-4 shrink-0" />
                      Choose a player
                    </button>
                  )}
                </div>
              );
            })}

            <Field error={nameError ?? undefined}>
              <FieldLabel htmlFor="team-name" required>
                Team Name
              </FieldLabel>
              <input
                id="team-name"
                type="text"
                className={styles.wizardInputCls}
                placeholder="Team 3"
                value={teamName}
                onChange={(e) => {
                  setTeamName(e.target.value);
                  setNameError(null);
                }}
              />
            </Field>

            {apiError && <ApiError message={apiError} />}
          </div>
        </div>

        <div className="flex shrink-0 flex-col gap-3 border-t border-border px-6 py-4">
          <OpenRoundCompletionNotice message={openRoundCompletionNotice} />
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="rounded-md font-bold text-primary"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => void handleSubmit()}
              disabled={!isComplete || isSaving}
              title={
                isComplete
                  ? undefined
                  : `Choose all ${slotCount} players before adding the team.`
              }
              className="rounded-md bg-primary font-bold text-white"
            >
              {isSaving ? (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  Saving…
                </>
              ) : (
                "Add Team"
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
